/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers, OnFormInitialValuesChange } from '../../../screens/utils';
import { useFlowTypeChecker } from '../../../services/common/miscUtils';
import { MixedComponentsProps } from '../../../types/mixedComponentProps';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from '../components/formInputs';
import { CustomerDetailsFormModel_PL } from './formModel';
import { getInitialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';
import { GenderEnum, MaritalStatusEnum } from '../../../types/forms';

export const CustomerDetailsFormNew_PL: React.FC<MixedComponentsProps> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<CustomerDetailsFormModel_PL>>();

  const flowTypeIs = useFlowTypeChecker();

  useEffect(() => {
    setValidationSchema(getValidationSchema());
  }, [flowTypeIs]);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    summaryMode,
    isEditing,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (/* values: ThisFormModel */) => {
    /* start loader */
    Promise.resolve(/* submit */).then(onFormSubmitSuccess).catch(/* on error */).finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t('customerDetailsForm:title:private'), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  onFormInitialValuesChange: OnFormInitialValuesChange;
  disabled: boolean;
  onValueChange?: (any) => void;
  formHelpers: FormHelpers;
};

const FormContent: React.FC<FormContentProps> = ({ onFormInitialValuesChange, disabled, formHelpers }) => {
  const { values, initialValues, validateForm, submitForm, setFieldValue } =
    useFormikContext<CustomerDetailsFormModel_PL>();
  const [inputs] = useState(createFormInputs('customerDetailsForm'));

  useEffect(() => {
    // TODO: Uncomment to handle prefill and initial section auto-focus
    // onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const isFemale = values.gender === GenderEnum.female;
  const isOrWasMarried = [MaritalStatusEnum.married, MaritalStatusEnum.divorced, MaritalStatusEnum.separated].includes(
    values.maritalStatus! /* Can be undefined but it's safe to ignore it */
  );

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disabled}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="5/12" s="1/1">
              <Button type="button" icon="address-book" secondary>
                {t('customerDetailsForm:searchForExistingCustomer')}
              </Button>
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.title()}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.gender()}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.maritalStatus()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.firstName()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.lastName()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.middleName()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.maidenName(isFemale, isOrWasMarried)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.dateOfBirth()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.email()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.countryCode()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.phoneNumber(true)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.correspondenceMethod()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.nationality()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <FormSectionSubmitButton disabled={disabled} onClick={submitForm} formHelpers={formHelpers} />
      </Fieldset>
    </Form>
  );
};
